import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ChartBarIcon,
  FolderIcon,
  UsersIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PlusIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { analyticsAPI, projectAPI } from '../services/api';
import LoadingSpinner from '../components/ui/LoadingSpinner';

// Mock data for demonstration
const mockDashboardData = {
  overview: {
    total_projects: 12,
    active_projects: 8,
    completed_projects: 4,
    total_tasks: 156,
    completed_tasks: 98,
    total_team_members: 15,
    success_rate: 87.5,
    total_executions: 45,
  },
  recent_projects: [
    {
      project_id: 'proj-1',
      title: 'E-commerce Platform Redesign',
      status: 'in_progress',
      progress: 75,
      team_size: 6,
      due_date: '2025-10-15',
      priority: 'high',
    },
    {
      project_id: 'proj-2', 
      title: 'Mobile App Development',
      status: 'planning',
      progress: 25,
      team_size: 4,
      due_date: '2025-11-30',
      priority: 'medium',
    },
    {
      project_id: 'proj-3',
      title: 'Data Migration Project',
      status: 'completed',
      progress: 100,
      team_size: 3,
      due_date: '2025-09-20',
      priority: 'critical',
    },
  ],
  alerts: [
    {
      id: 1,
      type: 'warning',
      message: 'Project "E-commerce Platform" is 2 days behind schedule',
      timestamp: '2025-09-25T10:30:00Z',
    },
    {
      id: 2,
      type: 'success',
      message: 'Data Migration Project completed successfully',
      timestamp: '2025-09-25T09:15:00Z',
    },
  ],
};

// Metric Card Component
function MetricCard({ title, value, total, icon: Icon, color, trend }) {
  const colorClasses = {
    primary: 'bg-primary-50 text-primary-600',
    success: 'bg-success-50 text-success-600',
    warning: 'bg-warning-50 text-warning-600',
    secondary: 'bg-secondary-50 text-secondary-600',
  };

  return (
    <div className="card">
      <div className="card-body">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <div className="flex items-baseline space-x-2">
              <p className="text-2xl font-bold text-gray-900">
                {value}
                {total && <span className="text-lg text-gray-500">/{total}</span>}
              </p>
              {trend && (
                <span className="text-sm font-medium text-success-600">
                  {trend}
                </span>
              )}
            </div>
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </div>
    </div>
  );
}

// Project Card Component
function ProjectCard({ project }) {
  const statusColors = {
    planning: 'bg-gray-100 text-gray-800',
    in_progress: 'bg-primary-100 text-primary-800',
    completed: 'bg-success-100 text-success-800',
    on_hold: 'bg-warning-100 text-warning-800',
  };

  const priorityColors = {
    low: 'text-gray-500',
    medium: 'text-warning-600',
    high: 'text-danger-600',
    critical: 'text-danger-700 font-semibold',
  };

  return (
    <Link
      to={`/projects/${project.project_id}`}
      className="block p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:shadow-sm transition-all"
    >
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-medium text-gray-900 truncate">
          {project.title}
        </h4>
        <span className={`status-badge ${statusColors[project.status]}`}>
          {project.status.replace('_', ' ')}
        </span>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
        <span>{project.team_size} team members</span>
        <span className={priorityColors[project.priority]}>
          {project.priority} priority
        </span>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Progress</span>
          <span className="font-medium">{project.progress}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-primary-600 h-2 rounded-full transition-all"
            style={{ width: `${project.progress}%` }}
          />
        </div>
      </div>
    </Link>
  );
}

// Alert Item Component
function AlertItem({ alert }) {
  const alertStyles = {
    warning: {
      bg: 'bg-warning-50',
      border: 'border-warning-200',
      icon: 'text-warning-600',
      text: 'text-warning-800',
    },
    success: {
      bg: 'bg-success-50',
      border: 'border-success-200',
      icon: 'text-success-600',
      text: 'text-success-800',
    },
    error: {
      bg: 'bg-danger-50',
      border: 'border-danger-200',
      icon: 'text-danger-600',
      text: 'text-danger-800',
    },
  };

  const style = alertStyles[alert.type] || alertStyles.warning;

  return (
    <div className={`p-3 rounded-lg border ${style.bg} ${style.border}`}>
      <div className="flex items-start space-x-3">
        <div className={`flex-shrink-0 ${style.icon}`}>
          {alert.type === 'warning' && <ExclamationTriangleIcon className="w-5 h-5" />}
          {alert.type === 'success' && <CheckCircleIcon className="w-5 h-5" />}
          {alert.type === 'error' && <ExclamationTriangleIcon className="w-5 h-5" />}
        </div>
        <div className="flex-1">
          <p className={`text-sm ${style.text}`}>{alert.message}</p>
          <p className="text-xs text-gray-500 mt-1">
            {new Date(alert.timestamp).toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  // Fetch dashboard data
  const { data: dashboardData, isLoading, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => analyticsAPI.getDashboard(),
    fallbackData: mockDashboardData,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading dashboard</h3>
        <p className="mt-1 text-sm text-gray-500">Please try refreshing the page.</p>
        <p className="mt-2 text-xs text-red-600">{error.message}</p>
      </div>
    );
  }

  // Use mock data if API data is not available
  const safeData = dashboardData || mockDashboardData;
  const { overview, recent_projects, alerts } = safeData;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Welcome back! Here's what's happening with your projects.</p>
        </div>
        <Link
          to="/projects/new"
          className="btn-primary flex items-center space-x-2"
        >
          <PlusIcon className="w-4 h-4" />
          <span>New Project</span>
        </Link>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Active Projects"
          value={overview.active_projects}
          total={overview.total_projects}
          icon={FolderIcon}
          color="primary"
          trend="+12%"
        />
        <MetricCard
          title="Completed Tasks"
          value={overview.completed_tasks}
          total={overview.total_tasks}
          icon={CheckCircleIcon}
          color="success"
          trend="+8%"
        />
        <MetricCard
          title="Team Members"
          value={overview.total_team_members}
          icon={UsersIcon}
          color="secondary"
          trend="+2"
        />
        <MetricCard
          title="Success Rate"
          value={`${overview.success_rate}%`}
          icon={ArrowTrendingUpIcon}
          color="warning"
          trend="+5%"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Projects */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="card-header">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Recent Projects</h3>
                <Link
                  to="/projects"
                  className="text-sm text-primary-600 hover:text-primary-700 flex items-center space-x-1"
                >
                  <span>View all</span>
                  <ArrowRightIcon className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                {recent_projects.map((project) => (
                  <ProjectCard key={project.project_id} project={project} />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Alerts & Activity */}
        <div className="space-y-6">
          {/* Alerts */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-gray-900">Alerts</h3>
            </div>
            <div className="card-body">
              <div className="space-y-3">
                {alerts.map((alert) => (
                  <AlertItem key={alert.id} alert={alert} />
                ))}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
            </div>
            <div className="card-body">
              <div className="space-y-3">
                <Link
                  to="/projects/new"
                  className="flex items-center p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
                >
                  <PlusIcon className="w-5 h-5 text-primary-600 mr-3" />
                  <span className="text-sm font-medium text-gray-900">Create New Project</span>
                </Link>
                <Link
                  to="/analytics"
                  className="flex items-center p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
                >
                  <ChartBarIcon className="w-5 h-5 text-primary-600 mr-3" />
                  <span className="text-sm font-medium text-gray-900">View Analytics</span>
                </Link>
                <Link
                  to="/team"
                  className="flex items-center p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
                >
                  <UsersIcon className="w-5 h-5 text-primary-600 mr-3" />
                  <span className="text-sm font-medium text-gray-900">Manage Team</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
